import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  differenceInHours,
  differenceInYears,
  differenceInMinutes,
  differenceInSeconds,
  formatDistance,
  Locale,
  formatDistanceToNow,
  formatISO,
  startOfToday,
  isBefore,
  isAfter,
  isToday,
  isEqual,
  add,
  differenceInMonths,
  endOfWeek,
  startOfWeek,
  formatDuration as formatDurationLib,
  parse,
  // eslint-disable-next-line import/no-duplicates
} from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { de, enGB, es, pt, ca, fr, it } from 'date-fns/locale';
import { useLanguage } from '../useLanguage';
import { useProvider } from '../useProvider';
import { getLocaleDateFormat } from './localeDateFormat';
import { DifferenceFormat } from '~/Models/DTO/Time.model';

type WeekStartIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;

export const getDateLocaleFromLanguage = (language: string) => {
  switch (language) {
    case 'en':
      return enGB;
    case 'de':
      return de;
    case 'ca':
      return ca;
    case 'fr':
      return fr;
    case 'it':
      return it;
    case 'es':
      return es;
    case 'pt':
      return pt;
    default:
      return enGB;
  }
};

export const dateDifferenceMinutes = (date: string, compareDate: string) =>
  differenceInMinutes(new Date(date), new Date(compareDate));
export const dateDifferenceHours = (date: string, compareDate: string) =>
  differenceInHours(new Date(date), new Date(compareDate));
export const dateDifferenceSeconds = (date: string, compareDate: string) =>
  differenceInSeconds(new Date(date), new Date(compareDate));
export const dateDifferenceMonths = (date: string, compareDate: string) =>
  differenceInMonths(new Date(date), new Date(compareDate));
export const dateDifferenceYears = (date: string, compareDate: string) =>
  differenceInYears(new Date(date), new Date(compareDate));

const differenceFunctions = [
  dateDifferenceSeconds,
  dateDifferenceMinutes,
  dateDifferenceHours,
  dateDifferenceMonths,
  dateDifferenceYears,
];

export const dateToProviderTimeZone = (date: string, timeZone: string) =>
  utcToZonedTime(new Date(date), timeZone);
export const dateToUtc = (date: string, timeZone: string) =>
  zonedTimeToUtc(new Date(date), timeZone);
export const dateToFormat = (date: string, pattern: string, timeZone: string, locale: Locale) =>
  format(new Date(date), pattern, { timeZone, locale });
export const fromDateToString = (date: Date, pattern: string) => format(date, pattern);
export const formatDistanceBetween = (date1: string, date2: string, locale: Locale) =>
  formatDistance(new Date(date1), new Date(date2), { addSuffix: true, locale });
export const formatDistanceNow = (now: string, locale: Locale) =>
  formatDistanceToNow(new Date(now), { addSuffix: true, locale });
export const formatToISO = (date: string) => formatISO(new Date(date));
export const beforeDate = (date1: string, compareDate: string) =>
  isBefore(new Date(date1), new Date(compareDate));
export const afterDate = (date1: string, compareDate: string) =>
  isAfter(new Date(date1), new Date(compareDate));
export const equalDate = (date1: string, compareDate: string) =>
  isEqual(new Date(date1), new Date(compareDate));
export const today = (date: string) => isToday(new Date(date));
export const addToDate = (date: string, amount: { [key: string]: number }) =>
  add(new Date(date), amount);
export const differenceBetweenDate = (
  date: string,
  compareDate: string,
  format: DifferenceFormat,
): number => differenceFunctions[format](date, compareDate);
export const getEndOfWeek = (date: string, weekStartsOn: WeekStartIndex = 1) =>
  endOfWeek(new Date(date), { weekStartsOn });
export const getStartOfWeek = (date: string, weekStartsOn: WeekStartIndex = 1) =>
  startOfWeek(new Date(date), { weekStartsOn });
export const getHoursAndMinutes = (date: string | Date) => {
  const dateObj = new Date(date);
  return {
    hour: dateObj.getHours(),
    // returns the minutes with two digits (e.g. 5 -> 05)
    minute: String(dateObj.getMinutes()).padStart(2, '0'),
  };
};

export const getTranslationKeyForDay = (day: Date) => {
  const weekIndex = dateToWeekIndex(day) as number;
  const dayKey = getDayOfWeek(weekIndex);
  return dayKey;
};
export const dateToWeekIndex = (date: Date) => {
  // Create a new Date object from the provided date string
  const currentDate = new Date(date);

  // Get the day of the week (0 for Sunday, 1 for Monday, and so on)
  let weekIndex = currentDate.getDay();

  // Adjust the index if your week starts on a different day (e.g., Monday)
  // You can customize this logic based on your requirements
  if (weekIndex === 0) {
    weekIndex = 6; // Sunday is usually considered the last day of the week
  } else {
    weekIndex -= 1; // Adjust the index to start from 0 (Monday as 0, Tuesday as 1, and so on)
  }

  return weekIndex;
};

export const getDayOfWeek = (date: number) => {
  const daysMap = {
    0: 'app.weekdays.monday',
    1: 'app.weekdays.tuesday',
    2: 'app.weekdays.wednesday',
    3: 'app.weekdays.thursday',
    4: 'app.weekdays.friday',
    5: 'app.weekdays.saturday',
    6: 'app.weekdays.sunday',
  } as any;
  return !!daysMap?.[date] ? daysMap[date] : daysMap[0];
};
export const useDateFns = () => {
  const { currentLanguage } = useLanguage();
  const { currentProvider } = useProvider();
  const currentLocale = getDateLocaleFromLanguage(currentLanguage);
  const convertToProviderTimezone = (date: string) =>
    dateToProviderTimeZone(date, currentProvider?.timeZoneId || '');
  const convertToUtc = (date: string) => dateToUtc(date, currentProvider?.timeZoneId || '');
  const formatDate = (date: string, pattern: string) =>
    dateToFormat(
      convertToProviderTimezone(date).toString(),
      pattern,
      currentProvider?.timeZoneId || '',
      currentLocale,
    );
  const formatFromDate = (date: Date, pattern: string) => fromDateToString(date, pattern);
  const formatDuration = (
    duration: Duration,
    options?: {
      format?: string[];
      zero?: boolean;
      delimiter?: string;
      locale?: Locale;
    },
  ): string => formatDurationLib(duration, { locale: currentLocale, ...(options || {}) });
  const formatDifferenceBetween = (date1: string, date2: string) =>
    formatDistanceBetween(date1, date2, currentLocale);
  const formatDifferenceToNow = (now: string) => formatDistanceNow(now, currentLocale);
  const nowAsUTC = () => formatToISO(new Date().toISOString());
  const startOfTodayAsUTCString = () =>
    formatToISO(convertToUtc(startOfToday().toString()).toString());
  const isBeforeDate = (date: string, compareDate: string) => beforeDate(date, compareDate);
  const isAfterDate = (date: string, compareDate: string) => afterDate(date, compareDate);
  const isDateToday = (date: string) => today(date);
  const isDateEqual = (date: string, compareDate: string) => equalDate(date, compareDate);
  const addTime = (date: string, amount: { [key: string]: number }) => addToDate(date, amount);
  const differenceBetween = (date: string, compareDate: string, format: DifferenceFormat) =>
    differenceBetweenDate(date, compareDate, format);

  const localeDateFormat = getLocaleDateFormat(currentLocale.code || 'en');

  const convertTimeStringToDate = (time: string, defaultTime?: string) => {
    const timeString = defaultTime ? defaultTime : time;
    return new Date(
      new Date().setHours(
        (timeString && Number(timeString.split(':')[0])) || 0,
        (timeString && Number(timeString.split(':')[1])) || 0,
      ),
    );
  };

  const formatStringToStandardTime = (hour: string, minute: string) => {
    let HH, MM;
    HH = hour;
    MM = minute;
    if (Number(hour) >= 24) {
      HH = `${Number(hour) - 24}`;
    }
    if (HH.length < 2) {
      HH = `0${HH}`;
    }
    if (MM.length < 2) {
      MM = `0${MM}`;
    }
    return `${HH}:${MM}:00`;
  };

  return {
    parse,
    formatDate,
    formatFromDate,
    formatDifferenceBetween,
    formatDifferenceToNow,
    convertToProviderTimezone,
    convertToUtc,
    nowAsUTC,
    startOfTodayAsUTCString,
    isBeforeDate,
    isAfterDate,
    isDateToday,
    isDateEqual,
    addTime,
    differenceBetween,
    formatDuration,
    currentLocale,
    localeDateFormat,
    convertTimeStringToDate,
    formatStringToStandardTime,
    getHoursAndMinutes,
  };
};
